import React from 'react';
import { FaTimes } from 'react-icons/fa';

export const VOTE_TYPES = [
  { value: 'single_choice', label: 'Single Choice', desc: 'Members can select only one option' },
  { value: 'multiple_choice', label: 'Multiple Choice', desc: 'Members can select multiple options' },
  { value: 'yes_no', label: 'Yes / No', desc: 'Simple approval voting' }
];

export function CreateVotingModal({ formData, setFormData, creating, onSubmit, onClose }) {
  const handleAddOption = () => {
    setFormData({ ...formData, options: [...formData.options, ''] });
  };

  const handleRemoveOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="create-voting-modal">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Create New Voting</h3>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
              placeholder="What are we voting on?"
              required
              data-testid="voting-title-input"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
              rows="2"
              placeholder="Provide more context..."
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Vote Type</label>
            <select
              value={formData.vote_type}
              onChange={(e) => setFormData({ ...formData, vote_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
              data-testid="voting-type-select"
            >
              {VOTE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              {VOTE_TYPES.find(t => t.value === formData.vote_type)?.desc}
            </p>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Options</label>
            {formData.options.map((opt, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder={`Option ${index + 1}`}
                  data-testid={`voting-option-${index}`}
                />
                {formData.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddOption}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Option
            </button>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">End Date (optional)</label>
            <input
              type="datetime-local"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allow_anonymous}
                onChange={(e) => setFormData({ ...formData, allow_anonymous: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700">Anonymous voting</span>
            </label>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="submit-voting-btn"
            >
              {creating ? 'Creating...' : 'Create Voting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
